<template>
  <div id="accordionNotification" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingNotification" class="accordion-header">
        <button
          aria-controls="collapseNotification"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseNotification"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
              <v-icon icon="mail" size="xs" class="text-white" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.sendNotification") }}</div>
              <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("clientTour.sendNotificationSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseNotification"
        aria-labelledby="headingNotification"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionNotification"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <form id="case-update-notification-form" class="d-flex flex-column justify-content-between h-100 mb-12">
            <fieldset class="mb-12">
              <legend class="visually-hidden">{{ $t("clientTour.notification") }}</legend>

              <div class="mb-12">
                <v-form-input-radio
                  v-model="paxs"
                  :formValueOptions="[
                    { key: false, label: $t('clientTour.sendOnlyClient') },
                    { key: true, label: $t('clientTour.sendClientAndPaxs') },
                  ]"
                  form-class="mb-4"
                />
              </div>

              <v-form id="case-type-notification-select" :label="$t('clientTour.notification')" form-type="outline" class="mb-0">
                <v-select
                  v-model="notification"
                  :clearable="false"
                  :options="clientEmailNotificationOptions"
                  :reduce="(returnValue: any) => returnValue.key"
                >
                  <template v-slot:no-options>
                    <div class="my-4">{{ $t("general.vueSelect.noOptions") }}</div>
                  </template>
                </v-select>
              </v-form>
            </fieldset>

            <v-button
              :disabled="disableButton"
              :is-loading="disableButton"
              class="btn-icon w-fit align-self-end"
              size="xs"
              variant="primary"
              @click="sendNotification"
            >
              {{ $t("general.button.send") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>
          </form>

          <div class="d-flex flex-column text-xs">
            <p class="text-xs fw-bold text-uppercase text-muted mb-8">{{ $t("clientTour.clientNotificationLog") }}</p>

            <div class="notification-log">
              <div v-if="!notificationsLoaded" class="position-relative py-48">
                <v-loader />
              </div>

              <template v-else-if="!!notifications && notifications.length > 0">
                <div
                  v-for="(notification, index) in notifications"
                  :key="notification.id"
                  class="notification-log-item"
                  :class="{ 'is-last': index === notifications.length - 1 }"
                >
                  <div class="notification-log-dot">
                    <v-icon icon="mail" size="xs" class="text-white" />
                  </div>

                  <div class="notification-log-content">
                    <div class="d-flex align-items-center justify-content-between flex-wrap gap-4">
                      <span class="fw-bold text-sm">{{ notification.email_type || notification.type || '—' }}</span>
                      <span class="badge bg-success-soft text-success text-xs">Sent</span>
                    </div>

                    <div class="text-muted text-xs mt-4">
                      <v-icon icon="person" size="xs" space="me-4" />
                      <template v-if="notification.sent_by">{{ notification.sent_by.name }}</template>
                      <span class="text-muted-subtle"> via </span>
                      <span class="fw-medium">{{ notification.email_from || 'booking@waw.travel' }}</span>
                    </div>

                    <div class="text-muted-subtle text-xs mt-4">
                      <v-icon icon="schedule" size="xs" space="me-4" />
                      {{ $str.formatDateTime(notification.created_at, settings.formatDatetime) }}
                    </div>
                  </div>
                </div>
              </template>

              <div v-else class="text-center text-muted italic py-24">
                {{ $t("clientTour.noClientNotifications") }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import vSelect from "vue-select";
import { VForm, VFormInputRadio } from "@uniqoders/form";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import { findConceptByKey, mapConcepts } from "@/helpers/ConceptHelper";
import useConcepts from "@/helpers/Concepts";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import Notification from "@/api/objects/Notification";
import { useAppStore } from "@/stores/app";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  agency: string;
  tour: number;
  clientTour: number;
  clientNotificationLogs?: any[];
}>();

const emit = defineEmits<{
  (e: "reloadCase"): void;
}>();

const { concepts } = useConcepts();
const appStore = useAppStore();

const settings = computed(() => appStore.settings);

const clientEmailNotificationOptions = computed(() => {
  if (concepts.value && concepts.value["client.email_notification_types"]) {
    return mapConcepts(concepts.value["client.email_notification_types"]);
  }
  return [];
});

const disableButton = ref(false);
const notification = ref("");
const paxs = ref(false);
const notifications = ref<Notification[]>([]);
const notificationsLoaded = ref(false);
const { t } = useI18n();
const toast = useToast();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;

onMounted(async () => {
  notification.value = clientEmailNotificationOptions.value[0]?.key ?? "";
  await loadTourNotifications();
});

const sendNotification = async () => {
  await $modal?.confirm({
    title: t("clientTour.emailModal.title"),
    text: t("clientTour.emailModal.text"),
    leftButtonClasses: "btn-tertiary",
    confirmButtonText: t("general.button.send"),
    confirmButtonCallback: () => doSendNotification(),
  });
};

const doSendNotification = async () => {
  try {
    disableButton.value = true;

    const data = {
      notification: notification.value,
      paxs: paxs.value,
    };

    await api.case.caseSendNotificationToClient(props.agency, props.clientTour, data);

    toast.success(t("general.shared.savedChanges"));
    emit("reloadCase");
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || "Unexpected error");
  } finally {
    disableButton.value = false;
    await loadTourNotifications();
  }
};

const loadTourNotifications = async () => {
  notifications.value = props.clientNotificationLogs || [];
  notificationsLoaded.value = true;
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

.notification-log {
  max-height: 23rem;
  overflow-y: auto;
  padding-right: 4px;
}

.notification-log-item {
  display: flex;
  gap: 12px;
  padding-bottom: 16px;
  position: relative;

  &:not(.is-last)::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 28px;
    bottom: 0;
    width: 2px;
    background-color: var(--uq-border-color, #e0e0e0);
  }
}

.notification-log-dot {
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--uq-primary, #0d6efd);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.notification-log-content {
  flex: 1;
  min-width: 0;
}

.text-muted-subtle {
  color: var(--uq-text-muted, #9e9e9e);
}

.bg-success-soft {
  background-color: rgba(25, 135, 84, 0.12);
}
</style>
